import React, {forwardRef, useState, useEffect, useRef} from 'react'
import {Box, chakra, useMultiStyleConfig} from '@chakra-ui/react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import PropTypes from 'prop-types'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {CustomInput} from './partials/custom-input'
import DatePickerHeader from './partials/date-picker-header'
import {localeMap} from './date-picker-utils'

export function getDateFnsLocale(siteLocale) {
    if (!siteLocale) return 'en-GB'
    const normalizedLocale = siteLocale.toLowerCase()
    return localeMap[normalizedLocale] || 'en-GB'
}

export function getStatus({isSuccess, isDisabled}) {
    let status = null
    status ??= isSuccess && 'success'
    status ??= !isSuccess && 'error'
    if (isDisabled) status = 'disabled'
    return status
}

function removeAriaLabelAndRoleOnMonthChange() {
    const monthElement = document.querySelector('.react-datepicker__month')
    if (monthElement) {
        monthElement.removeAttribute('aria-label')
        monthElement.removeAttribute('role')
    }
}
function dayRoleChange() {
    const dayElements = document.querySelectorAll('.react-datepicker__day')
    dayElements.forEach((day) => {
        day.setAttribute('role', 'button')
    })
}

const ChakraDatePicker = chakra(ReactDatePicker)

const DatePicker = forwardRef(
    (
        {
            value,
            onChange,
            isDisabled,
            isEditForm,
            placeholder,
            maxDate,
            calendarIcon,
            isSuccess,
            isFieldRequired = null,
            ...rest
        },
        ref,
    ) => {
        const {locale} = useMultiSite()
        const [isCalendarOpen, setIsCalendarOpen] = useState(false)
        const status = getStatus({isSuccess, isDisabled})
        const styles = useMultiStyleConfig('DatePicker', {variant: status})
        const calendarRef = useRef(null)

        useEffect(() => {
            if (isCalendarOpen && calendarRef.current) {
                removeAriaLabelAndRoleOnMonthChange()
                dayRoleChange()
            }
        }, [isCalendarOpen])

        return (
            <Box __css={styles} w="100%" ref={calendarRef}>
                <ChakraDatePicker
                    ref={ref}
                    selected={value}
                    onChange={onChange}
                    locale={getDateFnsLocale(locale?.id)}
                    dateFormat="P"
                    disabled={isEditForm || isDisabled}
                    placeholderText={placeholder}
                    maxDate={maxDate}
                    onCalendarOpen={() => setIsCalendarOpen(true)}
                    onCalendarClose={() => setIsCalendarOpen(false)}
                    onMonthChange={() => {
                        removeAriaLabelAndRoleOnMonthChange()
                        dayRoleChange()
                    }}
                    shouldCloseOnSelect
                    focusSelectedMonth
                    customInput={
                        <CustomInput
                            placeholder={placeholder}
                            calendarIcon={calendarIcon}
                            styles={styles}
                            isCalendarOpen={isCalendarOpen}
                            isFieldRequired={isFieldRequired}
                            ariaInvalid={rest['aria-invalid']}
                            ariaDescribedBy={rest['aria-describedby']}
                        />
                    }
                    renderCustomHeader={(props) => (
                        <DatePickerHeader {...props} locale={locale?.id} />
                    )}
                    {...rest}
                />
            </Box>
        )
    },
)

DatePicker.displayName = 'DatePicker'

DatePicker.propTypes = {
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    onChange: PropTypes.func,
    isDisabled: PropTypes.bool,
    isEditForm: PropTypes.bool,
    placeholder: PropTypes.string,
    maxDate: PropTypes.object,
    calendarIcon: PropTypes.node,
    isSuccess: PropTypes.bool,
    isFieldRequired: PropTypes.bool | undefined,
}

export default DatePicker
